import { Component } from "react";

import { reportFatalError } from "../utils/reportFatalError";

const messageStyle = {
  margin: "32px 0 0",
  padding: "0 20px",
  fontWeight: 400,
  fontSize: "16px",
  color: "#374151",
  textAlign: "left",
};

/**
 * ErrorBoundary Component
 *
 * A React error boundary component that catches JavaScript errors anywhere in its child component tree,
 * logs those errors to Sentry, and displays a fallback UI instead of crashing the whole app.
 *
 * Features:
 * - Catches errors in the component tree below it
 * - Reports errors to Sentry for monitoring
 * - Displays a fallback UI when an error occurs
 *
 * Usage:
 * ```jsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * @component
 * @example
 * // Wrap your application or specific components
 * import ErrorBoundary from './components/ErrorBoundary';
 *
 * function App() {
 *   return (
 *     <ErrorBoundary>
 *       <MainContent />
 *     </ErrorBoundary>
 *   );
 * }
 */

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // PostHog too, because Sentry is going and this is the only record a React
    // render crash leaves anywhere. `window.onerror` does NOT see these: React
    // catches the throw during rendering, so the boundary is the one and only
    // place this error is observable. Without this line, the failure mode that
    // blanks a buyer's screen would vanish entirely when Sentry is removed.
    //
    // componentDidCatch, not render(): this fires once per error, whereas
    // render() runs again on every subsequent re-render of the fallback.
    reportFatalError(error, {
      component: "react.error_boundary",
      clientLogExtra: {
        log_source: "react.error_boundary",
        log_component_stack: errorInfo?.componentStack?.slice(0, 2000),
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            minHeight: "100vh",
            backgroundColor: "rgb(240, 240, 240)",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "520px",
              minHeight: "100vh",
              backgroundColor: "#ffffff",
            }}
          >
            <p style={messageStyle}>Something went wrong.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
